import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthComplete } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { LoadingButton } from '../common/LoadingComponents';
import { validation } from '../../utils/helpers';
import { ROUTES } from '../../utils/constants';

function RegisterForm() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    bio: '',
    acceptedTOS: false,
    acceptedCodeOfConduct: false,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const { register, isAuthenticated, error, clearError } = useAuthComplete();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Password requirements checker
  const checkPasswordRequirements = (password) => {
    return {
      minLength: password.length >= 8,
      hasLowerCase: /[a-z]/.test(password),
      hasUpperCase: /[A-Z]/.test(password),
      hasNumberOrSymbol: /[0-9!@#$%^&*(),.?":{}|<>]/.test(password)
    };
  };

  const passwordRequirements = checkPasswordRequirements(formData.password);
  const passwordStrength = Object.values(passwordRequirements).filter(Boolean).length;
  const passwordStrengthPercent = (passwordStrength / 4) * 100;
  
  // Check if passwords match
  const passwordsMatch = formData.password && formData.confirmPassword && 
    formData.password === formData.confirmPassword;
  const passwordsDontMatch = formData.confirmPassword && 
    formData.password !== formData.confirmPassword;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || ROUTES.HOME;
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount/unmount

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: fieldValue }));
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
    
    // Clear general error
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Email validation
    const emailError = validation.validateEmail(formData.email);
    if (emailError) errors.email = emailError;
    
    // Password validation
    const passwordError = validation.validatePassword(formData.password);
    if (passwordError) errors.password = passwordError;
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    // Display name validation
    const displayNameError = validation.validateDisplayName(formData.displayName);
    if (displayNameError) errors.displayName = displayNameError;
    
    // Bio validation (optional)
    if (formData.bio && formData.bio.length > 500) {
      errors.bio = 'Bio must be less than 500 characters';
    }
    
    // TOS validation
    if (!formData.acceptedTOS) {
      errors.acceptedTOS = 'You must accept the Terms of Service';
    }
    
    // Code of Conduct validation
    if (!formData.acceptedCodeOfConduct) {
      errors.acceptedCodeOfConduct = 'You must accept the Code of Conduct';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const result = await register({
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        displayName: formData.displayName.trim(),
        bio: formData.bio.trim(),
        acceptedTOS: formData.acceptedTOS,
        acceptedCodeOfConduct: formData.acceptedCodeOfConduct,
      });
      
      if (result.success) {
        toast.success('Account created successfully! Welcome to QDN.');
        const from = location.state?.from?.pathname || ROUTES.HOME;
        navigate(from, { replace: true });
      } else if (result.error && result.error.includes('email already exists')) {
        // Email already exists - redirect to login with pre-filled email
        toast.info('An account with this email already exists. Please log in.');
        navigate(ROUTES.LOGIN, { 
          state: { 
            email: formData.email.toLowerCase().trim(),
            message: 'An account with this email already exists. Please enter your password to log in.'
          } 
        });
      }
    } catch (err) {
      console.error('Registration error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <h1>Join QDN</h1>
          <p>Create your account to get started</p>
        </div>

        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={fieldErrors.email ? 'error' : ''}
              placeholder="Enter your email"
              autoComplete="email"
              required
            />
            {fieldErrors.email && (
              <span className="field-error">{fieldErrors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="displayName">Display Name</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              className={fieldErrors.displayName ? 'error' : ''}
              placeholder="How should others see your name?"
              autoComplete="name"
              required
            />
            {fieldErrors.displayName && (
              <span className="field-error">{fieldErrors.displayName}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              className={fieldErrors.password ? 'error' : ''}
              placeholder="Create a secure password"
              autoComplete="new-password"
              required
            />
            
            {passwordFocused && (
              <div className="password-requirements">
                <div className="password-strength-bar">
                  <div 
                    className={`password-strength-fill strength-${passwordStrength}`}
                    style={{ width: `${passwordStrengthPercent}%` }}
                  />
                </div>
                <div className="password-strength-label">
                  Password strength: {passwordStrengthPercent}%
                </div>
                <ul className="requirements-list">
                  <li className={passwordRequirements.minLength ? 'met' : ''}>
                    <span className="requirement-icon">{passwordRequirements.minLength ? '✓' : '○'}</span>
                    At least 8 characters
                  </li>
                  <li className={passwordRequirements.hasLowerCase ? 'met' : ''}>
                    <span className="requirement-icon">{passwordRequirements.hasLowerCase ? '✓' : '○'}</span>
                    At least one lowercase letter
                  </li>
                  <li className={passwordRequirements.hasUpperCase ? 'met' : ''}>
                    <span className="requirement-icon">{passwordRequirements.hasUpperCase ? '✓' : '○'}</span>
                    At least one uppercase letter
                  </li>
                  <li className={passwordRequirements.hasNumberOrSymbol ? 'met' : ''}>
                    <span className="requirement-icon">{passwordRequirements.hasNumberOrSymbol ? '✓' : '○'}</span>
                    At least one number or symbol
                  </li>
                </ul>
              </div>
            )}
            
            {fieldErrors.password && (
              <span className="field-error">{fieldErrors.password}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-match-wrapper">
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`${
                  fieldErrors.confirmPassword ? 'error' : 
                  passwordsDontMatch ? 'mismatch' : 
                  passwordsMatch ? 'match' : ''
                }`}
                placeholder="Confirm your password"
                autoComplete="new-password"
                required
              />
              {passwordsMatch && (
                <span className="password-match-icon match" aria-label="Passwords match">
                  ✓
                </span>
              )}
              {passwordsDontMatch && (
                <span className="password-match-icon mismatch" aria-label="Passwords do not match">
                  ✕
                </span>
              )}
            </div>
            {passwordsDontMatch && (
              <span className="field-warning">Passwords do not match</span>
            )}
            {fieldErrors.confirmPassword && (
              <span className="field-error">{fieldErrors.confirmPassword}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="bio">Bio (optional)</label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              className={fieldErrors.bio ? 'error' : ''}
              placeholder="Tell us a bit about yourself..."
              rows="3"
              maxLength="500"
            />
            {fieldErrors.bio && (
              <span className="field-error">{fieldErrors.bio}</span>
            )}
            <small className="char-count">
              {formData.bio.length}/500 characters
            </small>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="acceptedTOS"
                checked={formData.acceptedTOS}
                onChange={handleInputChange}
                className={fieldErrors.acceptedTOS ? 'error' : ''}
              />
              <span>
                I accept the{' '}
                <Link to={ROUTES.TERMS_OF_SERVICE} target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </Link>
              </span>
            </label>
            {fieldErrors.acceptedTOS && (
              <span className="field-error">{fieldErrors.acceptedTOS}</span>
            )}
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="acceptedCodeOfConduct"
                checked={formData.acceptedCodeOfConduct}
                onChange={handleInputChange}
                className={fieldErrors.acceptedCodeOfConduct ? 'error' : ''}
              />
              <span>
                I accept the{' '}
                <Link to={ROUTES.CODE_OF_CONDUCT} target="_blank" rel="noopener noreferrer">
                  Code of Conduct
                </Link>
              </span>
            </label>
            {fieldErrors.acceptedCodeOfConduct && (
              <span className="field-error">{fieldErrors.acceptedCodeOfConduct}</span>
            )}
          </div>

          <LoadingButton
            type="submit"
            className="submit-btn"
            loading={isSubmitting}
            loadingText="Creating account..."
            disabled={isSubmitting}
          >
            Create Account
          </LoadingButton>
        </form>

        <div className="register-footer">
          <p>
            Already have an account?{' '}
            <Link to={ROUTES.LOGIN} state={location.state}>
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterForm;